import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../context/LanguageContext";
import { Copy, Printer, BookOpen, ScrollText, X } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import SaveToNotebookButton from "./SaveToNotebookButton";
import Markdown from "./Markdown";
import ShareMenu from "./share/ShareMenu";
import ShareCard from "./share/ShareCard";
import LessonPrintDoc from "./share/LessonPrintDoc";

/**
 * LessonView — pure presentational component that renders the structured
 * lesson JSON returned by /api/lessons/build.
 *
 * The lesson is ALWAYS source-grounded: every per_commentator item carries
 * its own ref and the sources_used list is rendered at the bottom. If a
 * commentator has `not_available: true`, we display a polite "not available"
 * message rather than hide it — this is required by the product spec so the
 * user sees the full picture of what was / wasn't consulted.
 *
 * When the lesson spans BOTH the Tanakh and the Mishnah (i.e. the basket
 * was mixed), the per-commentator section grows a small "לפי מקור / לפי
 * סדר ההוספה" toggle. In "לפי מקור" mode (default) the commentators are
 * split into two coloured sub-blocks — "מן המקרא" (navy) and
 * "מן המשנה" (teal) — so a printed page reads like a classical
 * דף מקורות. Pure-Tanakh and pure-Mishnah lessons skip the toggle entirely.
 */

/**
 * Detect whether a per_commentator item belongs to the Mishnah corpus
 * based on its `ref` string ("Mishnah Berakhot 1:1") OR the explicit
 * Hebrew Sefaria-ref string the backend produces ("משנה ברכות א:א").
 * Pirkei Avot is the lone Mishnah ref without the "Mishnah" prefix.
 *
 * NOTE: we intentionally do NOT match "מסכת " here — both Mishnah and
 * Talmud emit refs prefixed with "מסכת" (they share the same Hebrew
 * tractate names), so leaving the disambiguation to isTalmudRef first
 * keeps the Mishnah filter conservative. The English "Mishnah X" form
 * is unambiguous.
 */
function isMishnahRef(ref) {
  if (!ref || typeof ref !== "string") return false;
  return /^\s*(Mishnah\s|משנה\s|Pirkei\s+Avot|פרקי\s+אבות)/i.test(ref);
}

/**
 * Detect whether a per_commentator item belongs to the Talmud Bavli.
 * Talmud refs look like "Berakhot 2a:1" / "ברכות ב׳ ע״א:א" — distinguishable
 * from a Tanakh ref by the amud letter (a/b or ע״א/ע״ב after the daf
 * number). Order of checks runs BEFORE isMishnahRef so a ref like
 * "מסכת ברכות דף ב׳ ע״א" wins the Talmud bucket even though it shares
 * the "מסכת" prefix with the Mishnah corpus.
 */
function isTalmudRef(ref) {
  if (!ref || typeof ref !== "string") return false;
  // English Talmud ref: "<Tractate> <daf><a|b>" (with or without :segment)
  if (/\b\d+[ab]\b/i.test(ref)) return true;
  // Hebrew Talmud ref: the amud marker "ע״א" / "ע״ב" (or unicode geresh
  // variants) is unique to the Talmud — Mishnah refs never carry it.
  if (/ע\s*[\u05F4""'׳][\s]*[אב]|דף\s+/.test(ref)) return true;
  return false;
}
export default function LessonView({ lesson, meta, onClose }) {
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  const hasComm = Array.isArray(lesson?.per_commentator) && lesson.per_commentator.length > 0;
  const sources = lesson?.sources_used || [];

  const plainText = useMemo(() => buildPlainText(lesson, meta, t), [lesson, meta, t]);
  const previewCardRef = useRef(null);
  const printDocRef = useRef(null);

  // Detect whether the lesson is a *mixed* one (Tanakh / Mishnah /
  // Talmud). Only mixed lessons get the layout toggle; pure-corpus
  // lessons render unchanged. isMishnahRef is checked first so a
  // "Mishnah X 1:1" ref isn't mistaken for a Talmud ref (which also
  // contains a colon — see isTalmudRef for the disambiguation rule).
  const { isMixed, tanakhItems, mishnahItems, talmudItems } = useMemo(() => {
    const all = lesson?.per_commentator || [];
    const t = [], m = [], g = [];
    for (const c of all) {
      // Talmud is checked FIRST: Mishnah and Talmud share the same Hebrew
      // tractate names ("מסכת ברכות"), so if we ran isMishnahRef first
      // every Talmud ref would silently land in the Mishnah bucket.
      if (isTalmudRef(c.ref)) g.push(c);
      else if (isMishnahRef(c.ref)) m.push(c);
      else t.push(c);
    }
    const corpora = (t.length > 0 ? 1 : 0) + (m.length > 0 ? 1 : 0) + (g.length > 0 ? 1 : 0);
    return { isMixed: corpora > 1, tanakhItems: t, mishnahItems: m, talmudItems: g };
  }, [lesson]);

  // Default to "by source" because that's what reads cleanest on a דף
  // מקורות. Persisted only in component state — the user's last choice
  // is fine to forget between visits since the toggle is fast.
  const [groupMode, setGroupMode] = useState("source");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      toast.success(t("lesson_view.copied_toast"));
    } catch {
      toast.error(t("lesson_view.copy_failed"));
    }
  };

  // window.print() relies on the @media print stylesheet to swap from
  // the on-screen LessonView (which lives inside a Dialog with a
  // backdrop) to the off-screen flat LessonPrintDoc. The print CSS
  // hides everything except [data-testid="lesson-print-doc"] so the
  // duplication / cropping bugs from the previous implementation are
  // gone.
  const printLesson = () => {
    if (!printDocRef.current) {
      toast.error(t("lesson_view.print_not_ready"));
      return;
    }
    window.print();
  };

  // Build a short share-card body (title + opening hook or simple
  // meaning + first commentator). Full lesson is never crammed into a
  // 1080×1080 — for that we use the PDF (multi-page).
  const previewBody = useMemo(() => {
    const lines = [];
    if (lesson?.opening_hook) lines.push(truncate(lesson.opening_hook, 280));
    else if (lesson?.simple_meaning) lines.push(truncate(lesson.simple_meaning, 280));
    else if (lesson?.intro) lines.push(truncate(lesson.intro, 280));
    return lines;
  }, [lesson]);

  return (
    <article
      dir="rtl"
      data-testid="lesson-view"
      className="relative rounded-sm border border-gold/30 bg-card shadow-book p-6 md:p-10 overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-l from-transparent via-gold/60 to-transparent" />

      {/* Header + actions */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="space-y-2">
          <p className="font-latin italic text-[11px] tracking-[0.35em] uppercase text-gold">
            {t("lesson_view.header_brand")}
          </p>
          <h2
            data-testid="lesson-title"
            className="font-heading text-3xl md:text-4xl text-foreground leading-tight"
          >
            {lesson?.title || t("lesson_view.default_title")}
          </h2>
          {lesson?.subtitle && (
            <p className="font-heading text-sm text-muted-foreground">
              {lesson.subtitle}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Save lesson to notebook — saves the structured lesson with
              full markdown body so the learner can revisit it forever.
              We tag as `source_type: "ai"` (matches existing AI-generated
              entries) and surface the lesson title prominently. */}
          <SaveToNotebookButton
            variant="icon"
            testIdSuffix="lesson"
            className="text-muted-foreground hover:text-gold"
            payload={{
              source_type: "ai",
              source_book: meta?.book,
              source_chapter: meta?.chapter,
              source_verse: meta?.verse,
              source_ref_he: meta?.ref_he,
              source_label: t("lesson_view.default_save_label", { title: lesson?.title || t("lesson_view.default_title") }),
              title: lesson?.title || t("lesson_view.default_title"),
              content_text: plainText,
              content_he: lesson?.title || "",
            }}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={copy}
            data-testid="lesson-copy-btn"
            aria-label={t("lesson_view.aria_copy")}
            className="text-muted-foreground hover:text-gold"
          >
            <Copy className="h-4 w-4" strokeWidth={1.5} />
          </Button>
          <ShareMenu
            getImageNode={() => previewCardRef.current}
            getPdfNode={() => printDocRef.current}
            getText={() => plainText}
            filename={`torahya-shiur-${slugify(lesson?.title || "lesson")}`}
            title={lesson?.title || t("lesson_view.default_title")}
            testIdSuffix="lesson"
            pdfMultiPage
            iconOnly
            label={t("lesson_view.label_share")}
            buttonClassName=""
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={printLesson}
            data-testid="lesson-print-btn"
            aria-label={t("lesson_view.aria_print")}
            className="text-muted-foreground hover:text-gold"
          >
            <Printer className="h-4 w-4" strokeWidth={1.5} />
          </Button>
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              data-testid="lesson-close-btn"
              aria-label={t("lesson_view.aria_close")}
              className="text-muted-foreground hover:text-gold"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          )}
        </div>
      </div>

      <div className="ornamental-divider mb-6">✦ ❖ ✦</div>

      {/* Opening hook — engaging entry point */}
      {lesson?.opening_hook && (
        <Section title={t("lesson_view.section_opening_hook")} testid="lesson-section-opening-hook">
          <p className="body-text italic text-foreground/90">{lesson.opening_hook}</p>
        </Section>
      )}

      {/* Intro */}
      {lesson?.intro && (
        <Section title={t("lesson_view.section_intro")} testid="lesson-section-intro">
          <p className="body-text">{lesson.intro}</p>
        </Section>
      )}

      {/* Simple meaning */}
      {lesson?.simple_meaning && (
        <Section title={t("lesson_view.section_simple")} testid="lesson-section-simple">
          <p className="body-text">{lesson.simple_meaning}</p>
        </Section>
      )}

      {/* Per-commentator */}
      {hasComm && (
        <Section
          title={t("lesson_view.section_per_commentator")}
          testid="lesson-section-commentators"
          headerExtra={
            isMixed ? (
              <div
                className="inline-flex items-center rounded-sm border border-gold/30 bg-background/40 overflow-hidden text-[11px] font-heading"
                data-testid="lesson-grouping-toggle"
                role="tablist"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={groupMode === "source"}
                  data-testid="lesson-grouping-toggle-source"
                  onClick={() => setGroupMode("source")}
                  className={`px-2.5 py-1 transition-colors ${
                    groupMode === "source"
                      ? "bg-gold/20 text-gold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t("lesson_view.by_source")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={groupMode === "order"}
                  data-testid="lesson-grouping-toggle-order"
                  onClick={() => setGroupMode("order")}
                  className={`px-2.5 py-1 transition-colors border-r border-gold/20 ${
                    groupMode === "order"
                      ? "bg-gold/20 text-gold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t("lesson_view.by_order_added")}
                </button>
              </div>
            ) : null
          }
        >
          {isMixed && groupMode === "source" ? (
            <div className="space-y-6">
              {tanakhItems.length > 0 && (
                <CorpusGroup
                  title={t("lesson_view.group_mikra_title")}
                  subtitle={t("lesson_view.group_mikra_subtitle")}
                  // Navy bar matches Tanakh book spines — same #1a2b4a as
                  // the BookshelfSection Tanakh palette so the lesson reads
                  // visually consistent with the Library.
                  accent="#1a2b4a"
                  items={tanakhItems}
                  testid="lesson-corpus-group-tanakh"
                />
              )}
              {mishnahItems.length > 0 && (
                <CorpusGroup
                  title={t("lesson_view.group_mishnah_title")}
                  subtitle={t("lesson_view.group_mishnah_subtitle")}
                  // Teal matches Mishnah book spines (#23807a).
                  accent="#23807a"
                  items={mishnahItems}
                  testid="lesson-corpus-group-mishnah"
                />
              )}
              {talmudItems.length > 0 && (
                <CorpusGroup
                  title={t("lesson_view.group_talmud_title")}
                  subtitle={t("lesson_view.group_talmud_subtitle")}
                  // Oxblood-brown matches Talmud book spines (#4a2c19).
                  accent="#4a2c19"
                  items={talmudItems}
                  testid="lesson-corpus-group-talmud"
                />
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {lesson.per_commentator.map((c, i) => (
                <CommentaryCard key={i} c={c} index={i} />
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Comparison */}
      {lesson?.comparison && (
        lesson.comparison.agreements ||
        lesson.comparison.differences ||
        (lesson.comparison.focus_per_commentator || []).length > 0
      ) && (
        <Section title={t("lesson_view.section_comparison")} testid="lesson-section-comparison">
          {lesson.comparison.agreements && (
            <div className="mb-3">
              <h5 className="font-heading text-sm text-foreground mb-1">
                {t("lesson_view.agreements_header")}
              </h5>
              <p className="body-text">{lesson.comparison.agreements}</p>
            </div>
          )}
          {lesson.comparison.differences && (
            <div className="mb-3">
              <h5 className="font-heading text-sm text-foreground mb-1">
                {t("lesson_view.differences_header")}
              </h5>
              <p className="body-text">{lesson.comparison.differences}</p>
            </div>
          )}
          {(lesson.comparison.focus_per_commentator || []).length > 0 && (
            <div>
              <h5 className="font-heading text-sm text-foreground mb-1">
                {t("lesson_view.focus_header")}
              </h5>
              <ul className="space-y-1">
                {lesson.comparison.focus_per_commentator.map((f, i) => (
                  <li key={i} className="body-text">
                    <span className="text-gold font-heading">
                      {(isEn ? (f.commentator || f.commentator_he) : (f.commentator_he || f.commentator))}:
                    </span>{" "}
                    {f.focus}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Section>
      )}

      {/* Deep development — the heart of the shiur */}
      {lesson?.deep_development && (
        <Section title={t("lesson_view.section_deep_development")} testid="lesson-section-deep-development">
          <Markdown className="body-text">{lesson.deep_development}</Markdown>
        </Section>
      )}

      {/* Main idea */}
      {lesson?.main_idea && (
        <Section title={t("lesson_view.section_main_idea")} testid="lesson-section-main-idea">
          <p className="body-text font-heading text-lg text-foreground">
            {lesson.main_idea}
          </p>
        </Section>
      )}

      {/* Modern relevance */}
      {lesson?.modern_relevance && (
        <Section title={t("lesson_view.section_modern_relevance")} testid="lesson-section-modern-relevance">
          <p className="body-text">{lesson.modern_relevance}</p>
        </Section>
      )}

      {/* Takeaway */}
      {lesson?.takeaway && (
        <Section title={t("lesson_view.section_takeaway")} testid="lesson-section-takeaway">
          <p className="body-text">{lesson.takeaway}</p>
        </Section>
      )}

      {/* Questions */}
      {(lesson?.questions || []).length > 0 && (
        <Section title={t("lesson_view.section_questions")} testid="lesson-section-questions">
          <ol className="list-decimal pr-5 space-y-1.5">
            {lesson.questions.map((q, i) => (
              <li key={i} className="body-text">
                {q}
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Sources used */}
      {sources.length > 0 && (
        <Section title={t("lesson_view.section_sources")} testid="lesson-section-sources">
          <ul className="space-y-1">
            {sources.map((s, i) => (
              <li
                key={i}
                className="flex items-center gap-2 text-sm text-muted-foreground"
              >
                <ScrollText className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                <span className="font-heading">
                  {isEn ? (s.commentator || s.commentator_he) : (s.commentator_he || s.commentator)}
                </span>
                {s.ref && (
                  <span className="font-latin italic text-[11px] opacity-70">
                    — {s.ref}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-muted-foreground italic">
            {t("lesson_view.summary_footer")}
          </p>
        </Section>
      )}

      {/* Off-screen share targets — kept inside the article so they
          tear down with it. The print stylesheet (index.css → @media
          print) brings the LessonPrintDoc back on-screen during
          window.print() and hides everything else. */}
      <div
        aria-hidden
        data-print-host="lesson"
        style={{
          position: "fixed",
          left: -99999,
          top: 0,
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        <ShareCard
          ref={previewCardRef}
          size="square"
          lang={isEn ? "en" : "he"}
          eyebrow={meta?.ref_he
            ? t("lesson_view.eyebrow_with_ref", { ref: meta.ref_he })
            : t("lesson_view.eyebrow_only")}
          title={lesson?.title || t("lesson_view.default_title")}
          body={previewBody}
          footnote={t("lesson_view.share_footnote")}
          truncated
        />
        <LessonPrintDoc ref={printDocRef} lesson={lesson} meta={meta} />
      </div>
    </article>
  );
}

function slugify(s) {
  return String(s || "lesson")
    .toLowerCase()
    .replace(/[^a-z0-9א-ת]+/gi, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40) || "lesson";
}

function truncate(s, n) {
  const t = String(s || "");
  return t.length <= n ? t : t.slice(0, n - 1).trim() + "…";
}

function Section({ title, children, testid, headerExtra }) {
  return (
    <section className="mb-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <BookOpen className="h-4 w-4 text-gold" strokeWidth={1.5} />
        <h3 className="font-heading text-lg text-foreground">{title}</h3>
        {headerExtra && <div className="ms-auto">{headerExtra}</div>}
      </div>
      <div className="ps-6 border-s border-gold/15">{children}</div>
    </section>
  );
}

/**
 * Single commentary card — extracted so both the flat list and the
 * source-grouped list render the same DOM. Keeps existing testids and
 * styles untouched so the Tanakh-only fast path is byte-for-byte
 * unchanged from before this feature shipped.
 */
function CommentaryCard({ c }) {
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  return (
    <div
      data-testid={`lesson-commentator-${(c.commentator || "x").replace(/ /g, "-")}`}
      className="rounded-sm border border-gold/20 bg-background/60 p-4"
    >
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h4 className="font-heading text-lg text-gold">
          {isEn ? (c.commentator || c.commentator_he) : (c.commentator_he || c.commentator)}
        </h4>
        {c.ref && (
          <span className="font-latin italic text-[11px] text-muted-foreground tracking-wider">
            {c.ref}
          </span>
        )}
      </div>
      {c.not_available ? (
        <p className="text-sm text-muted-foreground italic">
          {t("lesson_view.no_commentary_for_verse")}
        </p>
      ) : (
        <>
          <p className="body-text">{c.summary}</p>
          {c.insight && (
            <p className="body-text mt-2 text-foreground/85 italic border-r-2 border-gold/40 pr-3">
              ✦ {c.insight}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Visual group of commentary cards belonging to one corpus, with a
 * colour bar on the right (RTL → opens the row visually like a
 * classical bound volume's spine peeking out the side of a sheet).
 */
function CorpusGroup({ title, subtitle, accent, items, testid }) {
  const { t } = useTranslation();
  return (
    <div data-testid={testid} className="rounded-sm overflow-hidden">
      <header
        className="flex items-baseline justify-between gap-3 px-3 py-2"
        style={{
          background: `linear-gradient(to left, ${accent}26, transparent)`,
          borderRight: `4px solid ${accent}`,
        }}
      >
        <h4 className="font-heading text-base text-foreground">
          {title}
          <span className="text-muted-foreground font-latin italic text-[11px] ms-2 tracking-wider">
            · {subtitle}
          </span>
        </h4>
        <span className="font-heading text-[11px] text-muted-foreground">
          {t("lesson_view.n_commentators", { count: items.length })}
        </span>
      </header>
      <div className="space-y-3 pt-3 pb-1">
        {items.map((c, i) => (
          <CommentaryCard key={i} c={c} />
        ))}
      </div>
    </div>
  );
}

function buildPlainText(lesson, meta, t) {
  if (!lesson) return "";
  const lines = [];
  lines.push(lesson.title || t("lesson_view.default_title"));
  if (lesson.subtitle) lines.push(lesson.subtitle);
  lines.push("");
  if (lesson.intro) lines.push(t("lesson_view.copy_intro"), lesson.intro, "");
  if (lesson.simple_meaning) lines.push(t("lesson_view.copy_simple"), lesson.simple_meaning, "");
  if ((lesson.per_commentator || []).length > 0) {
    lines.push(t("lesson_view.copy_per_commentator"));
    for (const c of lesson.per_commentator) {
      const name = c.commentator_he || c.commentator;
      const ref = c.ref ? ` [${c.ref}]` : "";
      if (c.not_available) {
        lines.push(`• ${name}${ref}: ${t("lesson_view.copy_no_commentary_line")}`);
      } else {
        lines.push(`• ${name}${ref}:`, c.summary || "", "");
      }
    }
  }
  if (lesson.comparison?.agreements)
    lines.push(t("lesson_view.copy_agreements"), lesson.comparison.agreements, "");
  if (lesson.comparison?.differences)
    lines.push(t("lesson_view.copy_differences"), lesson.comparison.differences, "");
  if (lesson.main_idea) lines.push(t("lesson_view.copy_main_idea"), lesson.main_idea, "");
  if (lesson.takeaway) lines.push(t("lesson_view.copy_takeaway"), lesson.takeaway, "");
  if ((lesson.questions || []).length > 0) {
    lines.push(t("lesson_view.copy_questions"));
    lesson.questions.forEach((q, i) => lines.push(`${i + 1}. ${q}`));
  }
  if ((lesson.sources_used || []).length > 0) {
    lines.push("", t("lesson_view.copy_sources"));
    for (const s of lesson.sources_used) {
      lines.push(`• ${s.commentator_he || s.commentator}${s.ref ? " — " + s.ref : ""}`);
    }
  }
  if (meta?.provider) lines.push("", `— ${meta.provider}/${meta.model}`);
  return lines.join("\n");
}
